import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useAuth, useUser } from "@clerk/react";
import { Analytics } from "@vercel/analytics/react";
import {
  useTheme,
  THEMES,
  DARK_BG,
  DARK_TEXT,
  LIGHT_BG,
  LIGHT_TEXT,
} from "./state/themeState.js";
import { useViewSettings } from "./state/viewSettingsState.js";
import { useRoomData } from "./hooks/useRoomData.js";
import { BrandLogo } from "./components/BrandLogo.js";
import { FlatView } from "./components/FlatView.js";
import { WelcomeModal, useWelcomeModal } from "./components/WelcomeModal.js";
import { Sidebar } from "./components/Sidebar.js";
import DoorOpen from "./components/common/Icons.js";
import { SourcePanel } from "./components/SourcePanel.js";
import { NavMenu } from "./components/NavMenu.js";
import { GROUPS } from "./groups.js";
import styles from "./styles/App.module.css";
import "./index.css";

const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

export function DesktopApp() {
  const { themeIndex, hoveredThemeIndex, isDark } = useTheme();
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const role = (user?.publicMetadata as { role?: string } | undefined)?.role;
  const isAdmin = role === "admin" || role === "editor";
  const { columnCount, grouping } = useViewSettings();
  const { show: showWelcome, dismiss: dismissWelcome } = useWelcomeModal();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dragSrcId, setDragSrcId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  const contentRef = useRef<HTMLElement>(null);
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());
  const dragAllowed = useRef(false);

  const {
    isHomeRoom,
    roomId,
    snapshot,
    isLoading,
    favorites,
    userRooms,
    searchQuery,
    filteredSections,
    sortedSections,
    suppressed,
    alsoIn,
    matchCount,
    readIds,
    markRead,
    handleSwap,
    handleFavoriteToggle,
    handleGroupChange,
    handleSearch,
    handleSearchClear,
  } = useRoomData();

  useEffect(() => {
    if (snapshot) {
      setActiveSectionId(snapshot.sourceSnapshots[0]?.sourceId ?? null);
    }
  }, [snapshot]);

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
  }, [roomId]);

  useEffect(() => {
    const scroller = contentRef.current;
    if (!scroller) return;
    const handleScroll = () => {
      let current: string | null = null;
      for (const [id, el] of sectionRefs.current) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= 56) current = id;
      }
      if (current !== null) setActiveSectionId(current);
    };
    scroller.addEventListener("scroll", handleScroll, { passive: true });
    return () => scroller.removeEventListener("scroll", handleScroll);
  }, []);

  function registerRef(id: string) {
    return (el: HTMLElement | null) => {
      if (el) sectionRefs.current.set(id, el);
      else sectionRefs.current.delete(id);
    };
  }

  return (
    <div className={styles.app}>
      <header className={styles.header} data-header="">
        <div className={styles.headerLeft}>
          <Link to="/" className={styles.headerTitleSub}>
            <BrandLogo
              accentColor={
                THEMES[hoveredThemeIndex ?? themeIndex]?.[
                  isDark ? "dark" : "light"
                ].primary
              }
              textColor={isDark ? DARK_TEXT : LIGHT_TEXT}
              backgroundColor={isDark ? DARK_BG : LIGHT_BG}
            />
          </Link>
        </div>
        <div className={styles.headerRoomSelectWrapper}>
          <DoorOpen
            width={25}
            height={25}
            color="var(--color-primary)"
            className={styles.headerRoomIcon}
          />
          <select
            className={styles.headerRoomSelect}
            title="Select room"
            value={roomId}
            onChange={(e) => handleGroupChange(e.target.value)}
          >
            {GROUPS.map((g) => (
              <option key={g.id} value={g.id}>
                {g.title}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          className={styles.hamburger}
          aria-label="Open sidebar"
          onClick={() => setSidebarOpen((v) => !v)}
        >
          <span />
          <span />
          <span />
        </button>

        <NavMenu />
        {isLoading && <div className={styles.loadingBar} />}
      </header>

      {!snapshot ? (
        <div className={styles.loading}>Loading…</div>
      ) : (
        <div className={styles.body}>
          {sidebarOpen && (
            <div
              className={styles.sidebarBackdrop}
              onClick={() => setSidebarOpen(false)}
            />
          )}
          <Sidebar
            sections={sortedSections}
            readIds={readIds}
            suppressed={suppressed}
            activeSectionId={activeSectionId}
            selectedGroupId={isHomeRoom ? null : roomId}
            favorites={favorites}
            isSignedIn={!!isSignedIn}
            isAdmin={isAdmin}
            userRooms={userRooms}
            searchQuery={searchQuery}
            onGroupChange={handleGroupChange}
            onSwap={handleSwap}
            onFavoriteToggle={handleFavoriteToggle}
            onSearch={handleSearch}
            onSearchClear={handleSearchClear}
            matchCount={matchCount}
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            isHomeRoom={isHomeRoom}
          />

          <main
            ref={contentRef}
            data-content-scroller=""
            className={`${styles.content} ${grouping === "none" ? styles.contentFlat : ""}`}
            style={
              {
                "--column-count-raw": String(columnCount),
              } as React.CSSProperties
            }
          >
            {grouping === "none" ? (
              <FlatView
                sections={filteredSections}
                readIds={readIds}
                onRead={markRead}
                suppressed={suppressed}
                highlight={searchQuery || undefined}
                roomTitle={isHomeRoom ? "Home" : (GROUPS.find((g) => g.id === roomId)?.title ?? userRooms.find((r) => r.id === roomId)?.title ?? roomId)}
                roomUrl={`${window.location.origin}${isHomeRoom ? "/" : `/${GROUPS.find((g) => g.id === roomId) ? `~${roomId}` : roomId}`}`}
              />
            ) : (
              <div className={styles.contentInner}>
                {filteredSections.map((section) => {
                  const isDragging = dragSrcId === section.sourceId;
                  const isOver = dragOverId === section.sourceId && !isDragging;
                  return (
                    <div
                      key={section.sourceId}
                      className={styles.dragCell}
                      draggable
                      data-dragging={isDragging || undefined}
                      data-drag-over={isOver || undefined}
                      onMouseDown={(e) => {
                        dragAllowed.current = !!(
                          e.target as HTMLElement
                        ).closest("[data-drag-header]");
                      }}
                      onDragStart={(e) => {
                        if (!dragAllowed.current) {
                          e.preventDefault();
                          return;
                        }
                        setDragSrcId(section.sourceId);
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      onDragEnd={() => {
                        setDragSrcId(null);
                        setDragOverId(null);
                      }}
                      onDragEnter={() => setDragOverId(section.sourceId)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (dragSrcId && dragSrcId !== section.sourceId)
                          handleSwap(dragSrcId, section.sourceId);
                        setDragSrcId(null);
                        setDragOverId(null);
                      }}
                    >
                      <SourcePanel
                        section={section}
                        readIds={readIds}
                        onRead={markRead}
                        suppressed={suppressed}
                        alsoIn={alsoIn}
                        sectionRef={registerRef(section.sourceId)}
                        highlight={searchQuery || undefined}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </main>
        </div>
      )}
      {showWelcome && <WelcomeModal onDismiss={dismissWelcome} />}
      <Analytics />
    </div>
  );
}
